import React, { memo, useCallback, useMemo, useState } from "react";
import i18n from "@microdoing/shared/i18n";
import { FlatList, Pressable, StyleSheet } from "react-native";
import FastImage from "react-native-fast-image";
import { Box } from "../Box";
import { MainText } from "../MainText";
import Commentary, { CommentaryType } from "./Commentary";
import { PressableOpacity } from "../PressableOpacity";
import { pluralizeI18n } from "app/utils/plurialize";

const styles = StyleSheet.create({
  main: { flex: 1, display: "flex" },
  image: {
    height: 18,
    width: 18,
    borderRadius: 25,
  },
});

export interface ICommentaryResponsesProps {
  commentary: CommentaryType;
  scrollToBottom: () => void;
  isLastComment?: boolean;
  handleResponse?: ({
    stepCommentId,
    isModifier,
    comment,
    receiver,
  }) => () => void;
}

const CommentaryResponses = ({
  commentary,
  handleResponse = () => () => {},
  scrollToBottom,
  isLastComment,
}: ICommentaryResponsesProps) => {
  const [seeMoreComments, setSeeMoreComments] = useState(false);

  const handleSeeResponse = useCallback(() => {
    if (isLastComment) {
      setTimeout(() => scrollToBottom(), 150);
    }
    setSeeMoreComments(!seeMoreComments);
  }, []);

  const renderItem = useCallback(
    ({ item, index }) => (
      <Commentary
        key={index}
        commentary={item}
        handleResponse={handleResponse}
        marginTop={index === 0 ? "sm" : "xs"}
      />
    ),
    [],
  );

  const avatarSource = useMemo(() => {
    return {
      uri: commentary?.responses?.[0]?.user?.picture,
    };
  }, [commentary?.responses?.[0]?.user?.picture]);

  const handleKeyExtractor = useCallback((item, index) => item._id, []);

  return (
    <>
      {!!(commentary?.responses?.length > 0) && !seeMoreComments ? (
        <PressableOpacity onPress={handleSeeResponse}>
          <Box flexDirection="row" alignItems="center" marginTop="xs">
            <FastImage
              style={styles.image}
              source={avatarSource}
              resizeMode="cover"
            />
            <MainText variant="l3-medium" marginLeft="xs">
              {commentary?.responses?.[0]?.user?.firstName}
            </MainText>
            <MainText variant="l3-medium" color="neutral.500" marginLeft="xxs">
              {i18n.t("commentary-responses")}
            </MainText>
            <MainText variant="l3-bold" color="brand.500" marginLeft="xs">
              {commentary?.responses?.length}{" "}
              {pluralizeI18n("answer", commentary?.responses?.length)}
            </MainText>
          </Box>
        </PressableOpacity>
      ) : (
        !!seeMoreComments && (
          <FlatList
            keyExtractor={handleKeyExtractor}
            data={commentary?.responses}
            extraData={commentary?.responses}
            style={styles.main}
            renderItem={renderItem}
          />
        )
      )}
    </>
  );
};

export default memo(CommentaryResponses);
